import { Component, forwardRef, Input, OnInit, ViewChild } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgForm,
  Validator,
  Validators,
} from '@angular/forms';
import { AppConstants } from '@wz-constants';
import { AddressService } from '@wz-services';
import { AutoUnsubscribeComponent } from '@wz-utilities/auto-unsubscribe.component';
import {
  getControlWithValueChanges,
  markAllControlsAsDirtyAndUpdateValidity,
} from '@wz-utilities/form.utilities';
import {
  hasAtLeastLetterOrNumber,
  isStringNullOrWhiteSpace,
  isStringTrimmed,
} from '@wz-utilities/string.utilities';
import { FormConfigModels } from 'models/form-config.models';
import { FormsModels } from 'models/forms.model';
import { finalize } from 'rxjs/operators';
import { AddressModels } from '../../../../../models/address.models';

@Component({
  selector: 'wz-address-picker',
  templateUrl: './address-picker.component.html',
  styleUrls: ['./address-picker.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AddressPickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => AddressPickerComponent),
      multi: true,
    },
  ],
})
export class AddressPickerComponent
  extends AutoUnsubscribeComponent
  implements OnInit, ControlValueAccessor, Validator
{
  @ViewChild('addressForm', { static: true }) addressForm!: NgForm;
  @Input() disabled!: boolean;
  @Input() private _addressValue: AddressModels.Address | any = {};

  @Input() config!: FormConfigModels.AddressConfig | undefined;

  get addressValue(): AddressModels.Address {
    return this._addressValue;
  }

  set addressValue(val: AddressModels.Address) {
    this._addressValue = val;
    this.propagate();
  }

  @Input() isAlreadyFullAddress!: boolean;

  @Input() shouldValidate: boolean = true;

  public isFullAddressVisible!: boolean;

  @Input() addressRepository: 'wazari' | 'smalltox' | undefined = undefined;

  public isFetchingCities = false;
  public cityList!: FormsModels.SelectListItem[];

  formGroup: FormGroup | any;

  constructor(
    private addressService: AddressService,
    private fb: FormBuilder,
  ) {
    super();

    this.formGroup = this.createFormGroup();
  }

  ngOnInit() {
    if (this.isAlreadyFullAddress) {
      this.showFullAddress();
    }
  }

  private createFormGroup() {
    const codeInseeCtrl = getControlWithValueChanges(
      this.fb,
      { value: null, disabled: true },
      [Validators.required],
      (_codeInseeCtrl) => {
        this.propagate();
      },
      this.destroy$,
    );

    const codePostalCtrl = getControlWithValueChanges(
      this.fb,
      '',
      [Validators.required],
      (zipCtrl) => {
        if (zipCtrl.valid) {
          this.isFetchingCities = true;
          codeInseeCtrl.enable();
          this.addressService
            .getCities(zipCtrl.value, this.addressRepository)
            .pipe(
              //delayAtLeast(AppConstants.Delay.Fast),
              finalize(() => (this.isFetchingCities = false)),
            )
            .subscribe((res) => {
              this.cityList = res;
              if (res.length > 0) {
                if (
                  codeInseeCtrl.value &&
                  res.findIndex((c) => c.value === codeInseeCtrl.value) !== -1
                ) {
                  // do nothing
                } else {
                  let index = 0;
                  if (this._addressValue.cityName) {
                    index = res.findIndex(
                      (c) =>
                        c.text
                          .toLowerCase()
                          .indexOf(
                            this._addressValue.cityName.toLowerCase(),
                          ) !== -1,
                    );
                    if (index === -1) {
                      index = 0;
                    }
                  }

                  codeInseeCtrl.setValue(res[index].value);
                }
              }

              this.propagate();
            });
        } else {
          codeInseeCtrl.disable();
          this.cityList = [];
        }
      },
      this.destroy$,
      AppConstants.Delay.Medium,
    );
    const group = this.fb.group({
      codeInsee: codeInseeCtrl,
      cityZipCode: codePostalCtrl,
    });

    return group;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.addressForm.control.disable();
    } else {
      this.addressForm.control.enable();
    }
  }

  showFullAddress() {
    this.isFullAddressVisible = true;
  }

  onAddressValueChange(item: AddressModels.Address) {
    if (this.addressRepository) {
      if (item.cityZipCode) {
        this.formGroup.patchValue({
          cityZipCode: item.cityZipCode,
          codeInsee: item.codeInsee,
        });
      }
    }

    this.addressValue = item;
  }

  onTypeVoieClick(data: string) {
    this.addressValue.streetType = data;
  }

  propagate() {
    if (this.addressRepository) {
      const codeInsee = this.formGroup.get('codeInsee').value;
      const zipCode = this.formGroup.get('cityZipCode').value;

      this._addressValue.cityZipCode = zipCode;
      this._addressValue.codeInsee = codeInsee;
      if (this.cityList) {
        const city = this.cityList.find((cl) => cl.value == codeInsee);
        if (city) {
          this._addressValue.cityName = city.text;
        }
      }
    }

    this.propagateChange(this._addressValue);
  }

  /**
   * IMPLEMENTATION CONTROLVALUEACCESSOR
   */
  writeValue(value: any) {
    if (value !== undefined && value !== null) {
      this._addressValue = value;
      this.formGroup.patchValue({
        cityZipCode: value.cityZipCode,
        codeInsee: value.codeInsee,
      });
    } else {
      this._addressValue = {};
    }
  }

  propagateChange = (_: any) => {};

  registerOnChange(fn: (_: any) => any) {
    this.propagateChange = fn;
  }

  registerOnTouched() {}

  markAsDirty() {
    markAllControlsAsDirtyAndUpdateValidity(this.addressForm);
  }

  validate(_fc: FormControl) {
    if (!this.shouldValidate) {
      return null;
    }
    this.markAsDirty();
    let isInvalid =
      isStringNullOrWhiteSpace(this.addressValue.cityZipCode ?? '') ||
      !isStringTrimmed(this.addressValue.cityZipCode ?? '') ||
      isStringNullOrWhiteSpace(this.addressValue.cityName ?? '') ||
      !isStringTrimmed(this.addressValue.cityName ?? '') ||
      isStringNullOrWhiteSpace(this.addressValue.streetType ?? '') ||
      !isStringTrimmed(this.addressValue.streetType ?? '') ||
      isStringNullOrWhiteSpace(this.addressValue.streetName ?? '') ||
      !isStringTrimmed(this.addressValue.streetName ?? '') ||
      !hasAtLeastLetterOrNumber(this.addressValue.streetName ?? '') ||
      !hasAtLeastLetterOrNumber(this.addressValue.streetType ?? '');
    if (isInvalid) {
      return { invalid: true };
    }
    return null;
  }
}
